import React, { useState } from "react"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import DataTable from "aldoo-ra/DataTable"
import DropDownSelect from "aldoo-ra/DropdownSelect"
import { showNotification } from "aldoo-ra/Notification"
import TranscriptViewer from "./components/TranscriptViewer"
import { SessionsCRUD } from "./api"

const OUTCOME_BADGE = {
  converted:       "bg-admin_success/15 text-admin_success",
  browsing:        "bg-admin_info_bg text-admin_info",
  abandoned:       "bg-admin_neutral_1 text-admin_text_secondary",
  escalated:       "bg-admin_warning/15 text-admin_warning",
  email_collected: "bg-admin_primary/15 text-admin_primary",
  no_match:        "bg-admin_error/15 text-admin_error"
}

const TIER_BADGE = {
  high_value:   "bg-admin_warning/15 text-admin_warning",
  medium_value: "bg-admin_info_bg text-admin_info",
  low_value:    "bg-admin_neutral_1 text-admin_text_secondary",
  churning:     "bg-admin_warning/20 text-admin_warning",
  anonymous:    "bg-admin_neutral_1/50 text-admin_text_secondary/70"
}

function MetaSidebar({ session }) {
  const dur = session.duration ? `${Math.round(session.duration / 60)}m` : "—"
  const rows = [
    ["Session ID",  session.sessionId],
    ["Agent",       session.agentId],
    ["Visitor ID",  session.visitorId],
    ["Trigger",     session.trigger],
    ["Page",        session.triggerPage || "—"],
    ["Language",    session.language || "—"],
    ["Turns",       session.turnCount],
    ["Duration",    dur],
    ["Tier",        session.visitorTier],
    ["Outcome",     session.outcome],
    ["Started",     session.startedAt ? new Date(session.startedAt).toLocaleString() : "—"],
    ["Ended",       session.endedAt   ? new Date(session.endedAt).toLocaleString()   : "—"],
    ["LTV",         (session.visitorLtv || []).map(l => `${l.amount} ${l.currency}`).join(", ") || "—"],
    ["Email",       session.emailCollected || "—"],
  ]

  return (
    <div className="w-72 flex-shrink-0 border-l border-admin_bordered bg-admin_paper p-4 overflow-y-auto">
      <Typography variant="h4" className="mb-3">Session Info</Typography>
      <dl className="space-y-2">
        {rows.map(([label, value]) => (
          <div key={label}>
            <dt className="text-xs text-admin_text_secondary">{label}</dt>
            <dd className="text-xs text-admin_text font-medium break-all">{String(value)}</dd>
          </div>
        ))}
      </dl>
      {session.productsRecommended?.length > 0 && (
        <div className="mt-3">
          <dt className="text-xs text-admin_text_secondary mb-1">Products Recommended</dt>
          <div className="flex flex-wrap gap-1">
            {session.productsRecommended.map((pid) => (
              <span key={pid} className="text-xs bg-admin_neutral_1 px-2 py-0.5 rounded">{pid}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default function SessionBrowser() {
  const [selected, setSelected]     = useState(null)
  const [refreshKey, setRefreshKey] = useState(0)
  const [filters, setFilters]       = useState({ outcome: "", agentId: "" })

  const handleRowClick = async (row) => {
    const res = await SessionsCRUD.Get({ match: { _id: row._id } })
    const session = Array.isArray(res) ? res[0] : res?.data?.[0]
    if (session) setSelected(session)
    else showNotification({ message: "Failed to load session", className: "bg-admin_error text-admin_text_inv" })
  }

  const columns = [
    {
      id: "sessionId",
      header: "Session",
      headerClassName: "w-[22%]",
      render: (v) => <span className="text-xs font-mono text-admin_text">{v?.slice(0, 20)}…</span>
    },
    {
      id: "agentId",
      header: "Agent",
      headerClassName: "w-[18%]",
      render: (v) => <span className="text-xs text-admin_text">{v}</span>
    },
    {
      id: "visitorTier",
      header: "Tier",
      headerClassName: "w-[12%]",
      render: (v) => <span className={`text-xs px-2 py-0.5 rounded-full ${TIER_BADGE[v] || ""}`}>{v}</span>
    },
    {
      id: "outcome",
      header: "Outcome",
      headerClassName: "w-[14%]",
      sortable: true,
      render: (v) => <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${OUTCOME_BADGE[v] || ""}`}>{v}</span>
    },
    {
      id: "turnCount",
      header: "Turns",
      headerClassName: "w-[8%]",
      sortable: true,
      render: (v) => <span className="text-xs text-admin_text">{v}</span>
    },
    {
      id: "startedAt",
      header: "Started",
      headerClassName: "w-[18%]",
      sortable: true,
      render: (v) => <span className="text-xs text-admin_text_secondary">{v ? new Date(v).toLocaleString() : "—"}</span>
    },
    {
      id: "open",
      header: "",
      headerClassName: "w-[8%]",
      render: (_, row) => (
        <Button variant="secondary" size="sm" onClick={(e) => { e.stopPropagation(); handleRowClick(row) }}>
          View
        </Button>
      )
    }
  ]

  if (selected) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center gap-3 px-4 py-3 border-b border-admin_bordered flex-shrink-0">
          <Button variant="secondary" size="sm" onClick={() => setSelected(null)}>← Back</Button>
          <Typography variant="h4">Session Transcript</Typography>
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ml-auto ${OUTCOME_BADGE[selected.outcome] || ""}`}>
            {selected.outcome}
          </span>
        </div>
        <div className="flex flex-1 overflow-hidden">
          <div className="flex-1 overflow-hidden">
            <TranscriptViewer session={selected} />
          </div>
          <MetaSidebar session={selected} />
        </div>
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <Typography variant="h2">Sessions</Typography>
        <div className="flex gap-2 items-center">
          <DropDownSelect
            variant="admin"
            className="w-48"
            placeholder="All Outcomes"
            options={["converted","browsing","abandoned","escalated","email_collected","no_match"].map((o) => ({ value: o, label: o }))}
            value={filters.outcome ? { value: filters.outcome } : null}
            onChange={(sel) => setFilters((f) => ({ ...f, outcome: (Array.isArray(sel) ? sel[0] : sel)?.value || "" }))}
            allowEmptySelection={true}
          />
          <Button variant="secondary" size="sm" onClick={() => setRefreshKey((k) => k + 1)}>Refresh</Button>
        </div>
      </div>

      <DataTable
        refreshKey={refreshKey}
        renders={columns}
        apiMethod={SessionsCRUD.Get}
        requestFields={{ match: { ...(filters.outcome ? { outcome: filters.outcome } : {}) } }}
        onRowClick={handleRowClick}
      />
    </div>
  )
}
